/**
 * ArticlePeriodTrends — weekly and monthly trend section for the article
 * detail flyout. Mirrors the site-wide overview's period comparison tables
 * and bar charts, scoped to a single article/page.
 */

import React from 'react';
import { UrlDetail } from '../../../../services/performanceMetricsService';
import {
  fmtShortDate,
  weekDatesFromStarts,
  monthDatesFromStarts,
  PeriodBarChart,
  PeriodComparisonTable,
} from './PeriodTrends';

type PeriodMetric = { label: string; current: number; previous: number; inProgress: number };

function buildMetrics(
  impressions: number[],
  clicks: number[],
  sessions: number[],
  includeSessions: boolean,
): PeriodMetric[] {
  const n = Math.max(impressions.length, clicks.length, sessions.length);
  const metrics: PeriodMetric[] = [
    { label: 'Impressions', current: impressions[n - 2] || 0, previous: impressions[n - 3] || 0, inProgress: impressions[n - 1] || 0 },
    { label: 'Clicks', current: clicks[n - 2] || 0, previous: clicks[n - 3] || 0, inProgress: clicks[n - 1] || 0 },
  ];
  if (includeSessions) {
    metrics.push({ label: 'Sessions', current: sessions[n - 2] || 0, previous: sessions[n - 3] || 0, inProgress: sessions[n - 1] || 0 });
  }
  return metrics;
}

export const ArticlePeriodTrends: React.FC<{ data: UrlDetail }> = ({ data }) => {
  const monthlyImpressions = data.monthly_impressions || [];
  const monthlyClicks = data.monthly_clicks || [];
  const monthlySessions = data.monthly_sessions || [];
  const monthlyAiSessions = data.monthly_ai_sessions || [];
  const weeklyImpressions = data.weekly_impressions || [];
  const weeklyClicks = data.weekly_clicks || [];
  const weeklySessions = data.weekly_sessions || [];
  const weeklyAiSessions = data.weekly_ai_sessions || [];

  if (monthlyClicks.length === 0 && weeklyClicks.length === 0) return null;

  const weeklyDates = weekDatesFromStarts(data.weekly_period_starts || []);
  const monthlyDates = monthDatesFromStarts(data.monthly_period_starts || []);

  const wl = weeklyDates.length;
  const ml = monthlyDates.length;
  const weekLast = weeklyDates[wl - 2];
  const weekPrior = weeklyDates[wl - 3];
  const weekInProg = weeklyDates[wl - 1];
  const monthLast = monthlyDates[ml - 2];
  const monthPrior = monthlyDates[ml - 3];
  const monthInProg = monthlyDates[ml - 1];

  const hasSessions = weeklySessions.some(v => v > 0) || monthlySessions.some(v => v > 0);
  const hasAiSessions = weeklyAiSessions.some(v => v > 0) || monthlyAiSessions.some(v => v > 0);

  return (
    <div className="space-y-4">
      {/* ── MONTHLY ── */}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1">Monthly</div>

      <PeriodComparisonTable
        title="Month over Month"
        currentLabel="Last Month"
        currentSub={monthLast ? monthLast.start.toLocaleDateString(undefined, { month: 'long' }) : ''}
        previousLabel="Prior Month"
        previousSub={monthPrior ? monthPrior.start.toLocaleDateString(undefined, { month: 'long' }) : ''}
        inProgressNote={`${monthInProg ? monthInProg.start.toLocaleDateString(undefined, { month: 'long' }) : 'This month'} so far (partial — GSC lags ~2–3 days)`}
        metrics={buildMetrics(monthlyImpressions, monthlyClicks, monthlySessions, hasSessions)}
      />

      <div className="grid grid-cols-2 gap-3">
        <PeriodBarChart values={monthlyImpressions} color="#01bcd4" label="Monthly Impressions" periodLabel="last month" dates={monthlyDates} />
        <PeriodBarChart values={monthlyClicks} color="#16a34a" label="Monthly Clicks" periodLabel="last month" dates={monthlyDates} />
        {hasSessions && (
          <PeriodBarChart values={monthlySessions} color="#8b5cf6" label="Monthly Sessions" periodLabel="last month" dates={monthlyDates} />
        )}
        {hasAiSessions && (
          <PeriodBarChart values={monthlyAiSessions} color="#10b981" label="Monthly AI Sessions" periodLabel="last month" dates={monthlyDates} />
        )}
      </div>

      {/* ── WEEKLY ── */}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1">Weekly</div>

      <PeriodComparisonTable
        title="Week over Week"
        currentLabel="Last Week"
        currentSub={weekLast ? `${fmtShortDate(weekLast.start)} – ${fmtShortDate(weekLast.end)}` : ''}
        previousLabel="Prior Week"
        previousSub={weekPrior ? `${fmtShortDate(weekPrior.start)} – ${fmtShortDate(weekPrior.end)}` : ''}
        inProgressNote={`Week in progress${weekInProg ? ` (since ${fmtShortDate(weekInProg.start)})` : ''} · partial — GSC lags ~2–3 days`}
        metrics={buildMetrics(weeklyImpressions, weeklyClicks, weeklySessions, hasSessions)}
      />

      <div className="grid grid-cols-2 gap-3">
        <PeriodBarChart values={weeklyImpressions} color="#01bcd4" label="Weekly Impressions" periodLabel="last week" dates={weeklyDates} />
        <PeriodBarChart values={weeklyClicks} color="#16a34a" label="Weekly Clicks" periodLabel="last week" dates={weeklyDates} />
        {hasSessions && (
          <PeriodBarChart values={weeklySessions} color="#8b5cf6" label="Weekly Sessions" periodLabel="last week" dates={weeklyDates} />
        )}
        {hasAiSessions && (
          <PeriodBarChart values={weeklyAiSessions} color="#10b981" label="Weekly AI Sessions" periodLabel="last week" dates={weeklyDates} />
        )}
      </div>
    </div>
  );
};
